import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FILTER_TYPES, FIELD_TYPES } from '../constants';
import { GECKO_CONSTS, GeckoFields } from 'ui';
import GeckoDate from 'services/gecko-date';
import DayRangePicker from 'components/DayRangePicker';
import RelativeTime from 'components/RelativeTime';

import './index.scss';

const FILTER_VALUE_LABEL = 'Filter value';

const getOperatorType = (operator) => {
  const [type] = operator.split(':');
  return type;
};

const areOperatorsSameType = (operator1, operator2) => {
  const operator1Type = getOperatorType(operator1);
  const operator2Type = getOperatorType(operator2);
  return operator1Type === operator2Type;
};

const getFieldOperatorConfig = (operator, field) => {
  return field.operators.find(
    (fieldOperator) => fieldOperator.value === operator
  );
};

const areCustomOperatorFieldsDifferent = (
  operator1,
  operator2,
  filterField
) => {
  const operator1Config = getFieldOperatorConfig(operator1, filterField);
  const operator2Config = getFieldOperatorConfig(operator2, filterField);

  return operator1Config.value !== operator2Config.value;
};

const shouldValueBeCleared = (oldOperator, newOperator, filterField) => {
  const operatorHasChangedType =
    areOperatorsSameType(oldOperator, newOperator) === false;
  if (operatorHasChangedType === true) {
    return true;
  }
  const isNotCustomOperator = getOperatorType(newOperator) !== 'custom';
  if (isNotCustomOperator === true) {
    return false;
  }
  return areCustomOperatorFieldsDifferent(
    oldOperator,
    newOperator,
    filterField
  );
};

const fieldsPropType = PropTypes.arrayOf(
  PropTypes.shape({
    key: PropTypes.string,
    type: PropTypes.oneOf(Object.values(FILTER_TYPES)),
    label: PropTypes.string,
    choices: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
  })
);

class FilterSection extends Component {
  static propTypes = {
    fields: fieldsPropType,
    filter: PropTypes.shape({
      key: PropTypes.string,
      operator: PropTypes.string,
      value: PropTypes.array,
    }),
    onChange: PropTypes.func,
    onDelete: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = Object.assign({}, props.filter);
  }

  getOperators() {
    if (!this.state.key) {
      return [];
    }
    const operatorsMap = {
      [FILTER_TYPES.TEXT]: [
        { label: 'is', value: `${FIELD_TYPES.TEXT}:is` },
        { label: 'is not', value: `${FIELD_TYPES.TEXT}:is not` },
        { label: 'starts with', value: `${FIELD_TYPES.TEXT}:starts with` },
        { label: 'ends with', value: `${FIELD_TYPES.TEXT}:ends with` },
        { label: 'contains', value: `${FIELD_TYPES.TEXT}:contains` },
        { label: 'not contains', value: `${FIELD_TYPES.TEXT}:not contains` },
      ],
      [FILTER_TYPES.DATE]: [
        { label: 'is', value: `${FIELD_TYPES.DATE}:is` },
        { label: 'is after', value: `${FIELD_TYPES.DATE}:is after` },
        { label: 'is before', value: `${FIELD_TYPES.DATE}:is before` },
        { label: 'is between', value: `${FIELD_TYPES.DATE_RANGE}:is between` },
        {
          label: 'is not between',
          value: `${FIELD_TYPES.DATE_RANGE}:is not between`,
        },
        {
          label: 'is in the last',
          value: `${FIELD_TYPES.DATE_RELATIVE}:is in the last`,
        },
      ],
      [FILTER_TYPES.NUMBER]: [
        { label: 'is', value: `${FIELD_TYPES.NUMBER}:is` },
        {
          label: 'is greater than',
          value: `${FIELD_TYPES.NUMBER}:is greater than`,
        },
        { label: 'is less than', value: `${FIELD_TYPES.NUMBER}:is less than` },
      ],
      [FILTER_TYPES.CHOICE]: [
        { label: 'is', value: `${FIELD_TYPES.CHOICE}:is` },
        { label: 'is not', value: `${FIELD_TYPES.CHOICE}:is not` },
      ],
      [FILTER_TYPES.TEXT_ARRAY]: [
        { label: 'is', value: `${FIELD_TYPES.TEXT_ARRAY}:is` },
        { label: 'is not', value: `${FIELD_TYPES.TEXT_ARRAY}:is not` },
        {
          label: 'starts with',
          value: `${FIELD_TYPES.TEXT_ARRAY}:starts with`,
        },
        { label: 'ends with', value: `${FIELD_TYPES.TEXT_ARRAY}:ends with` },
        { label: 'contains', value: `${FIELD_TYPES.TEXT_ARRAY}:contains` },
        {
          label: 'not contains',
          value: `${FIELD_TYPES.TEXT_ARRAY}:not contains`,
        },
      ],
    };
    const type = this.props.fields.find((field) => field.key === this.state.key)
      .type;
    if (type === FILTER_TYPES.CUSTOM) {
      const customOperators = this.props.fields.find(
        (field) => field.key === this.state.key
      ).operators;

      if (customOperators.length > 0 && customOperators[0].value) {
        return customOperators;
      }

      return customOperators.map((operator) => {
        return { label: operator, value: `custom:${operator}` };
      });
    }

    if (type === FILTER_TYPES.CHOICE) {
      const field = this.props.fields.find(
        (field) => field.key === this.state.key
      );
      if (field && field.nullable) {
        return operatorsMap[type].concat([
          { label: 'is blank', value: `${FIELD_TYPES.CHOICE}:is blank` },
          {
            label: 'is not blank',
            value: `${FIELD_TYPES.CHOICE}:is not blank`,
          },
        ]);
      }
    }

    return operatorsMap[type] || [];
  }

  getFieldType() {
    // Return a dummy disabled field if no key or operator is picked
    if (!this.state.key || !this.state.operator) {
      return {
        label: FILTER_VALUE_LABEL,
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT_CREATE_MULTI,
        width: '55%',
        options: [],
        disabledWhen: () => true,
      };
    }

    const field = this.props.fields.find(
      (field) => field.key === this.state.key
    );

    // The default value fields for each filter type
    const defaultMap = {
      [FIELD_TYPES.TEXT]: {
        label: FILTER_VALUE_LABEL,
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT_CREATE_MULTI,
        className: 'FilterSection-or-selector',
        width: '55%',
        options: [],
      },
      [FIELD_TYPES.DATE]: {
        label: FILTER_VALUE_LABEL,
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_DATE,
        width: '55%',
        format: 'X',
        display_format: new GeckoDate().FORMATS.SHORT,
        placeholder: new GeckoDate().FORMATS.SHORT,
      },
      [FIELD_TYPES.DATE_RANGE]: {
        label: FILTER_VALUE_LABEL,
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        width: '55%',
        component: (
          <DayRangePicker
            format="X"
            displayFormat={new GeckoDate().FORMATS.SHORT}
          />
        ),
      },
      [FIELD_TYPES.DATE_RELATIVE]: {
        label: FILTER_VALUE_LABEL,
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        width: '55%',
        component: <RelativeTime />,
      },
      [FIELD_TYPES.CHOICE]: {
        label: FILTER_VALUE_LABEL,
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
        className: 'FilterSection-or-selector',
        placeholder: 'One of',
        width: '55%',
        options: field.choices,
      },
      [FIELD_TYPES.NUMBER]: {
        label: FILTER_VALUE_LABEL,
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_NUMBER,
        width: '55%',
      },
      [FIELD_TYPES.CUSTOM]: {
        label: FILTER_VALUE_LABEL,
        key: 'value',
        type: field.field_type
          ? field.field_type
          : GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        width: '55%',
        component: field.field_type ? null : field.field,
        options: field.choices ? field.choices : null,
      },
      [FIELD_TYPES.TEXT_ARRAY]: {
        key: 'value',
        type: field.choices
          ? GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI
          : GECKO_CONSTS.FIELD_TYPE_SELECT_CREATE_MULTI,
        className: 'FilterSection-or-selector',
        width: '55%',
        options: field.choices ? field.choices : [],
      },
    };
    const [type, subtype] = this.state.operator.split(':');
    const operator = field.operators?.find(
      (operator) => operator.value === this.state.operator
    );
    if (operator?.field) {
      return {
        label: FILTER_VALUE_LABEL,
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        width: '55%',
        component: operator.field,
      };
    }
    if (defaultMap[type]) {
      if (!this.requiresValueField(type, subtype)) {
        return {
          label: FILTER_VALUE_LABEL,
          key: 'value',
          type: GECKO_CONSTS.FIELD_TYPE_SELECT_CREATE_MULTI,
          width: '55%',
          options: this.props.fields.find(
            (field) => field.key === this.state.key
          ).choices,
          disabledWhen: () => true,
        };
      }

      return defaultMap[type];
    }

    // A final fallback field
    return {
      key: 'value',
      type: GECKO_CONSTS.FIELD_TYPE_SELECT_CREATE_MULTI,
      width: '55%',
      options: [],
    };
  }

  fields() {
    return [
      {
        label: 'Filter by',
        key: 'key',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT,
        width: '25%',
        placeholder: 'Filter',
        options: this.props.fields.map((field) => {
          return {
            label: field.label,
            value: field.key,
          };
        }),
      },
      {
        label: 'Condition',
        key: 'operator',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT,
        width: '20%',
        placeholder: 'Condition',
        options: this.getOperators(),
        disabledWhen: () => !this.state.key,
      },
    ].concat(this.getFieldType());
  }

  handler(key, value) {
    const stateChanges = { [key]: value };

    if (key === 'operator' && value === null) {
      stateChanges.value = null;
    }
    if (key === 'operator' && value !== null && this.state.operator !== null) {
      const currentField = this.props.fields.find((field) => {
        return field.key === this.state.key;
      });

      const shouldClearValue = shouldValueBeCleared(
        this.state.operator,
        value,
        currentField
      );
      if (shouldClearValue === true) {
        stateChanges.value = null;
      }
    }
    if (key === 'key' && value !== this.state.value) {
      stateChanges.value = null;
      stateChanges.operator = null;
    }
    this.setState(stateChanges, () => {
      if (this.state.value !== undefined && this.state.value !== null) {
        this.props.onChange(this.state);
      } else if (this.state.operator) {
        // Certain filters don't require a value field
        // so if there is no value and the field doesnt
        // require a value the fire onChange
        const [type, subtype] = this.state.operator.split(':');
        if (!this.requiresValueField(type, subtype)) {
          this.props.onChange(this.state);
        }
      }
    });
  }

  requiresValueField(type, subtype) {
    if (
      [FILTER_TYPES.CHOICE, FILTER_TYPES.TEXT_ARRAY].includes(type) &&
      ['is blank', 'is not blank'].includes(subtype)
    ) {
      this.state.value = null;
      return false;
    }

    return true;
  }

  render() {
    return (
      <div className="FilterSection">
        <GeckoFields
          fields={this.fields()}
          values={this.state}
          handler={this.handler.bind(this)}
        />
        <button
          type="button"
          className="Filter-add-button far fa-plus btn gecko-btn btn-outline-primary icon-only"
          onClick={this.props.onAdd}
        />
        <button
          type="button"
          className="Filter-delete-button far fa-trash-alt btn gecko-btn btn-outline-danger icon-only"
          onClick={this.props.onDelete}
        />
      </div>
    );
  }
}

export default FilterSection;
